package org.vreo.devicehub.features.userAuthentication.presentation.login

import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.viewmodel.koinViewModel

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle

import org.vreo.devicehub.features.userAuthentication.presentation.components.PasswordTextField
import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.application_version_label
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.login_backgroun
import devicehub.composeapp.generated.resources.login_button_title
import devicehub.composeapp.generated.resources.login_field_placeholder_email
import devicehub.composeapp.generated.resources.login_forgot_password_button_title
import devicehub.composeapp.generated.resources.login_register_button_title
import devicehub.composeapp.generated.resources.vreo_logo
import org.vreo.devicehub.features.userAuthentication.domain.UserRole


/**
 * User authentication screen root.
 *
 * @param viewModel view model
 */
@Composable
fun UserAuthenticationScreenRoot(
    viewModel: UserLoginViewModel = koinViewModel(),
    onUserSignInClicked: (String, UserRole) -> Unit,
    onNewUserRegistrationClicked: () -> Unit,
) {
    val state: UserLoginState by viewModel.state.collectAsStateWithLifecycle()

    UserAuthenticationLoginView(
        state = state,
        onAction = { action ->
            val actionRequest = when (action) {
                is UserLoginActions.OnRegisterClicked -> {
                    onNewUserRegistrationClicked()
                    action
                }

                is UserLoginActions.OnLoginClicked -> {
                    action.copy(handler = onUserSignInClicked)
                }

                else -> action
            }

            viewModel.onAction(actionRequest)
        },
    )
}

/**
 * User authentication login view.
 *
 * @param state state to present
 * @param onAction action handler
 */
@Composable
fun UserAuthenticationLoginView(
    state: UserLoginState,
    onAction: (UserLoginActions) -> Unit,
) {
    val focusManager = LocalFocusManager.current

    Box(modifier = Modifier.fillMaxSize()) {
        Image(
            painter = painterResource(Res.drawable.login_backgroun),
            contentDescription = null,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop,
        )

        // Conditional section for displaying loading and error states
        if (state.isLoading) {
            LoadingDialog()
        } else if (state.error != null) {
            ErrorDialog(
                dialogTitle = stringResource(Res.string.error_general_title),
                dialogText = state.error.asString(),
                onDismissRequest = {
                    onAction(UserLoginActions.OnClearError())
                },
                onConfirmation = {
                    onAction(UserLoginActions.OnClearError())
                }
            )
        }

        // Main content
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 24.dp)
            ) {
                // User authentication card
                LoginCard(
                    state = state,
                    focusManager = focusManager,
                    onAction = onAction,
                    modifier = Modifier
                        .widthIn(max = 600.dp)
                        .padding(16.dp)
                        .wrapContentSize()
                )
            }

            // Version label
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.BottomCenter
            ) {
                Text(
                    text = stringResource(Res.string.application_version_label) + ": ${state.version}",
                    style = MaterialTheme.typography.bodyMedium,
                    modifier = Modifier.padding(top = 8.dp, bottom = 8.dp)
                )
            }
        }
    }
}

@Composable
private fun LoginCard(
    state: UserLoginState,
    focusManager: FocusManager,
    onAction: (UserLoginActions) -> Unit,
    modifier: Modifier = Modifier,
) {
    Card(
        colors = CardDefaults.cardColors().copy(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        shape = RoundedCornerShape(24.dp),
        modifier = modifier,
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            HorizontalDivider(
                color = Color.Red,
                thickness = 8.dp,
            )

            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .padding(16.dp)
                    .widthIn(max = 600.dp)
            ) {
                Image(
                    painter = painterResource(Res.drawable.vreo_logo),
                    contentDescription = null,
                    contentScale = ContentScale.Inside,
                    modifier = Modifier.heightIn(max = 75.dp)
                )

                Text(
                    text = "Welcome to Vreo Device Hub",
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(vertical = 16.dp)
                )

                // Username text field
                TextField(
                    value = state.userName,
                    onValueChange = { onAction(UserLoginActions.OnUpdateUsername(it)) },
                    placeholder = { Text(stringResource(Res.string.login_field_placeholder_email)) },
                    colors = TextFieldDefaults.colors(
                        focusedContainerColor = Color.Transparent,
                        unfocusedContainerColor = Color.Transparent,
                        disabledContainerColor = Color.Transparent,
                        errorContainerColor = Color.Transparent
                    ),
                    singleLine = true,
                    trailingIcon = {
                        AnimatedVisibility(
                            visible = state.userName.isNotBlank()
                        ) {
                            IconButton(
                                onClick = {
                                    onAction(UserLoginActions.OnUpdateUsername(""))
                                }
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Close,
                                    contentDescription = null,
                                )
                            }
                        }
                    },
                    isError = state.emptyUsername,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Text,
                        imeAction = ImeAction.Next
                    ),
                    keyboardActions = KeyboardActions(
                        onNext = { focusManager.moveFocus(FocusDirection.Down) }
                    ),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 8.dp)
                )

                Column(
                    horizontalAlignment = Alignment.End,
                    modifier = Modifier.padding(bottom = 8.dp)
                ) {
                    // Password text field
                    PasswordTextField(
                        value = state.password,
                        onValueChange = { onAction(UserLoginActions.OnUpdatePassword(it)) },
                        isError = state.emptyPassword,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Password,
                            imeAction = ImeAction.Done
                        ),
                        keyboardActions = KeyboardActions(
                            onDone = {
                                focusManager.clearFocus()
                                onAction(UserLoginActions.OnLoginClicked())
                            }
                        ),
                        colors = TextFieldDefaults.colors(
                            focusedContainerColor = Color.Transparent,
                            unfocusedContainerColor = Color.Transparent,
                            disabledContainerColor = Color.Transparent,
                            errorContainerColor = Color.Transparent
                        ),
                        modifier = Modifier
                            .fillMaxWidth()
                    )

                    // Forgot password button
                    TextButton(
                        onClick = { onAction(UserLoginActions.OnForgotPasswordClicked()) },
                    ) {
                        Text(
                            stringResource(Res.string.login_forgot_password_button_title),
                        )
                    }
                }

                // Login button
                Button(
                    onClick = { onAction(UserLoginActions.OnLoginClicked()) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 8.dp)
                ) {
                    Text(stringResource(Res.string.login_button_title))
                }

                // Register button
                TextButton(
                    onClick = { onAction(UserLoginActions.OnRegisterClicked()) },
                    modifier = Modifier
                        .fillMaxWidth()
                ) {
                    Text(stringResource(Res.string.login_register_button_title))
                }
            }
        }
    }
}

@Composable
@Preview
fun UserAuthenticationLoginViewPreview() {
    MaterialTheme {
        UserAuthenticationLoginView(
            state = UserLoginState(
                userName = "",
                password = "",
            ),
            onAction = {},
        )
    }
}
